import React from 'react';
import { FiCloud, FiCloudRain, FiThermometer, FiWind } from 'react-icons/fi';

const isEmpty = (value) =>
    value == null || value === '' || value === 'N/A' || value === '—';

const formatModifiedAt = (value) => {
    if (isEmpty(value)) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    const formatted = new Intl.DateTimeFormat('fr-FR', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(date);
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

const ParamValue = ({ value, unit }) => {
    if (isEmpty(value)) {
        return <span className="cc-weather-param__empty">—</span>;
    }
    return (
        <span className="cc-weather-param__value-wrap">
            <span className="cc-weather-param__value">{value}</span>
            {unit ? <span className="cc-weather-param__unit">{unit}</span> : null}
        </span>
    );
};

const WeatherParamCard = ({
    icon: Icon,
    title,
    badge,
    threshold,
    thresholdLabel = 'Seuil de déclenchement',
    thresholdUnit,
    upperThreshold,
    showUpperThreshold = false,
    hours,
    showHours = true,
}) => {
    const unconfigured = isEmpty(threshold)
        && (!showUpperThreshold || isEmpty(upperThreshold))
        && (!showHours || isEmpty(hours));
    return (
        <div className={`cc-weather-param-card${unconfigured ? ' cc-weather-param-card--unconfigured' : ''}`}>
            <div className="cc-weather-param-card__header">
                <span className="cc-weather-param-card__icon" aria-hidden="true">
                    <Icon size={16} />
                </span>
                <span className="cc-weather-param-card__title">{title}</span>
                {badge ? <span className="cc-weather-param-card__mode">{badge}</span> : null}
            </div>
            <div className="cc-weather-param-card__body">
                <div className="cc-weather-param-card__field">
                    <div className="cc-weather-param-card__label">{thresholdLabel}</div>
                    <div className="cc-weather-param-card__value">
                        <ParamValue value={threshold} unit={thresholdUnit} />
                    </div>
                </div>
                {showUpperThreshold ? (
                    <div className="cc-weather-param-card__field">
                        <div className="cc-weather-param-card__label">Seuil haut</div>
                        <div className="cc-weather-param-card__value">
                            <ParamValue value={upperThreshold} unit={thresholdUnit} />
                        </div>
                    </div>
                ) : null}
                {showHours ? (
                    <div className="cc-weather-param-card__field">
                        <div className="cc-weather-param-card__label">Nombre d’heures de déclenchement</div>
                        <div className="cc-weather-param-card__value">
                            <ParamValue value={hours} unit={isEmpty(hours) ? null : 'h'} />
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

/**
 * Présentation UX des paramètres de déclenchement météo
 * (en-tête + 3 cartes Précipitations / Température / Vent).
 */
const WeatherTriggerParamsSection = ({
    triggerType,
    weatherParameter,
    lastModifiedBy,
    lastModifiedAt,
    precipThreshold,
    precipUnit = 'mm/h',
    precipHours,
    precipMode,
    precipUpperThreshold,
    temperatureThreshold,
    temperatureHours,
    windThreshold,
    windHours,
}) => {
    const triggerLabel = isEmpty(triggerType) ? null : String(triggerType);
    const weatherLabel = isEmpty(weatherParameter) || weatherParameter === 'N/A'
        ? null
        : (Array.isArray(weatherParameter)
            ? weatherParameter.filter(Boolean).join(', ')
            : String(weatherParameter));
    const modifiedBy = isEmpty(lastModifiedBy) ? null : String(lastModifiedBy);
    const modifiedAt = formatModifiedAt(lastModifiedAt);

    return (
        <div className="cc-weather-params">
            <div className="cc-weather-params__meta">
                <div className="cc-weather-params__meta-item">
                    <div className="cc-weather-params__meta-label">Type de déclenchement</div>
                    <div className="cc-weather-params__meta-value">
                        {triggerLabel ? (
                            <span className="cc-weather-params__type-badge">{triggerLabel}</span>
                        ) : (
                            <span className="cc-weather-param__empty">—</span>
                        )}
                    </div>
                </div>
                <div className="cc-weather-params__meta-item">
                    <div className="cc-weather-params__meta-label">Paramètre météo</div>
                    <div className="cc-weather-params__meta-value">
                        {weatherLabel || <span className="cc-weather-param__empty">—</span>}
                    </div>
                </div>
                <div className="cc-weather-params__meta-item">
                    <div className="cc-weather-params__meta-label">Dernière modification</div>
                    <div className="cc-weather-params__meta-value">
                        {modifiedBy || <span className="cc-weather-param__empty">—</span>}
                    </div>
                </div>
                <div className="cc-weather-params__meta-item">
                    <div className="cc-weather-params__meta-label">Date de modification</div>
                    <div className="cc-weather-params__meta-value">
                        {modifiedAt || <span className="cc-weather-param__empty">—</span>}
                    </div>
                </div>
            </div>

            <div className="cc-weather-params__conditions">
                <div className="cc-weather-params__conditions-header">
                    <FiCloud size={15} aria-hidden="true" />
                    <h3 className="cc-weather-params__conditions-title">Conditions de déclenchement</h3>
                </div>
                <div className="cc-weather-params__cards">
                    <WeatherParamCard
                        icon={FiCloudRain}
                        title="Précipitations"
                        badge={precipMode === 'cumulative' ? 'Cumul' : (precipMode === 'hourly' ? 'Horaire' : null)}
                        threshold={precipThreshold}
                        thresholdLabel={precipMode ? 'Seuil bas' : 'Seuil de déclenchement'}
                        thresholdUnit={precipUnit}
                        upperThreshold={precipUpperThreshold}
                        showUpperThreshold={Boolean(precipMode)}
                        hours={precipHours}
                        showHours={precipMode !== 'cumulative'}
                    />
                    <WeatherParamCard
                        icon={FiThermometer}
                        title="Température"
                        threshold={temperatureThreshold}
                        thresholdUnit="°C"
                        hours={temperatureHours}
                    />
                    <WeatherParamCard
                        icon={FiWind}
                        title="Vent"
                        threshold={windThreshold}
                        thresholdUnit="km/h"
                        hours={windHours}
                    />
                </div>
            </div>
        </div>
    );
};

export default WeatherTriggerParamsSection;
